/**
 * MGRS/UTM coordinate conversion utilities.
 * Ported from react-leaflet-mgrs-graticule/src/Coordinates.js
 */

import { forward } from 'mgrs';
import { UtmCoord, LatLng } from './types';

// WGS-84 ellipsoid constants
const a = 6378137.0; // Equatorial radius
const eccSquared = 0.00669438; // Eccentricity squared

/**
 * Convert UTM coordinates to Lat/Lng.
 */
export function utmToLl(
    easting: number,
    northing: number,
    zoneNumber: number,
    zoneLetter: string
): LatLng {
    const e1 = (1 - Math.sqrt(1 - eccSquared)) / (1 + Math.sqrt(1 - eccSquared));
    const x = easting - 500000.0; // Remove 500,000 meter offset for longitude
    let y = northing;

    // Adjust for southern hemisphere
    const isNorthernHemisphere = ['N', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'].indexOf(zoneLetter) !== -1;
    if (!isNorthernHemisphere) {
        y -= 10000000.0;
    }

    const LongOrigin = (zoneNumber - 1) * 6 - 180 + 3;
    const eccPrimeSquared = eccSquared / (1 - eccSquared);

    const M = y / 0.9996;
    const mu =
        M /
        (a * (1 - eccSquared / 4 - (3 * eccSquared * eccSquared) / 64 - (5 * eccSquared * eccSquared * eccSquared) / 256));

    const phi1Rad =
        mu +
        ((3 * e1) / 2 - (27 * e1 * e1 * e1) / 32) * Math.sin(2 * mu) +
        ((21 * e1 * e1) / 16 - (55 * e1 * e1 * e1 * e1) / 32) * Math.sin(4 * mu) +
        ((151 * e1 * e1 * e1) / 96) * Math.sin(6 * mu);

    const N1 = a / Math.sqrt(1 - eccSquared * Math.sin(phi1Rad) * Math.sin(phi1Rad));
    const T1 = Math.tan(phi1Rad) * Math.tan(phi1Rad);
    const C1 = eccPrimeSquared * Math.cos(phi1Rad) * Math.cos(phi1Rad);
    const R1 = (a * (1 - eccSquared)) / Math.pow(1 - eccSquared * Math.sin(phi1Rad) * Math.sin(phi1Rad), 1.5);
    const D = x / (N1 * 0.9996);

    let Lat =
        phi1Rad -
        ((N1 * Math.tan(phi1Rad)) / R1) *
            ((D * D) / 2 -
                ((5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * eccPrimeSquared) * D * D * D * D) / 24 +
                ((61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * eccPrimeSquared - 3 * C1 * C1) * D * D * D * D * D * D) / 720);
    Lat = toDegrees(Lat);

    let Long =
        (D -
            ((1 + 2 * T1 + C1) * D * D * D) / 6 +
            ((5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * eccPrimeSquared + 24 * T1 * T1) * D * D * D * D * D) / 120) /
        Math.cos(phi1Rad);
    Long = LongOrigin + toDegrees(Long);

    return { lat: Lat, lng: Long };
}

/**
 * Convert Lat/Lng to UTM coordinates.
 */
export function llToUtm(lat: number, lng: number): UtmCoord {
    const k0 = 0.9996;
    const LatRad = degToRad(lat);
    const LongRad = degToRad(lng);

    let ZoneNumber = Math.floor((lng + 180) / 6) + 1;

    // Make sure the longitude 180.00 is in Zone 60
    if (lng === 180) {
        ZoneNumber = 60;
    }

    // Special zone for Norway
    if (lat >= 56.0 && lat < 64.0 && lng >= 3.0 && lng < 12.0) {
        ZoneNumber = 32;
    }

    // Special zones for Svalbard
    if (lat >= 72.0 && lat < 84.0) {
        if (lng >= 0.0 && lng < 9.0) {
            ZoneNumber = 31;
        } else if (lng >= 9.0 && lng < 21.0) {
            ZoneNumber = 33;
        } else if (lng >= 21.0 && lng < 33.0) {
            ZoneNumber = 35;
        } else if (lng >= 33.0 && lng < 42.0) {
            ZoneNumber = 37;
        }
    }

    const LongOrigin = (ZoneNumber - 1) * 6 - 180 + 3;
    const LongOriginRad = degToRad(LongOrigin);

    const eccPrimeSquared = eccSquared / (1 - eccSquared);

    const N = a / Math.sqrt(1 - eccSquared * Math.sin(LatRad) * Math.sin(LatRad));
    const T = Math.tan(LatRad) * Math.tan(LatRad);
    const C = eccPrimeSquared * Math.cos(LatRad) * Math.cos(LatRad);
    const A = Math.cos(LatRad) * (LongRad - LongOriginRad);

    const M =
        a *
        ((1 - eccSquared / 4 - (3 * eccSquared * eccSquared) / 64 - (5 * eccSquared * eccSquared * eccSquared) / 256) *
            LatRad -
            ((3 * eccSquared) / 8 + (3 * eccSquared * eccSquared) / 32 + (45 * eccSquared * eccSquared * eccSquared) / 1024) *
                Math.sin(2 * LatRad) +
            ((15 * eccSquared * eccSquared) / 256 + (45 * eccSquared * eccSquared * eccSquared) / 1024) *
                Math.sin(4 * LatRad) -
            ((35 * eccSquared * eccSquared * eccSquared) / 3072) * Math.sin(6 * LatRad));

    let UTMEasting =
        k0 *
            N *
            (A +
                ((1 - T + C) * A * A * A) / 6.0 +
                ((5 - 18 * T + T * T + 72 * C - 58 * eccPrimeSquared) * A * A * A * A * A) / 120.0) +
        500000.0;

    let UTMNorthing =
        k0 *
        (M +
            N *
                Math.tan(LatRad) *
                ((A * A) / 2 +
                    ((5 - T + 9 * C + 4 * C * C) * A * A * A * A) / 24.0 +
                    ((61 - 58 * T + T * T + 600 * C - 330 * eccPrimeSquared) * A * A * A * A * A * A) / 720.0));

    if (lat < 0.0) {
        UTMNorthing += 10000000.0; // 10000000 meter offset for southern hemisphere
    }

    return {
        northing: Math.round(UTMNorthing),
        easting: Math.round(UTMEasting),
        zoneNumber: ZoneNumber,
        zoneLetter: getLetterDesignator(lat),
    };
}

/**
 * Wrapper around MGRS forward function.
 * @param point [Lng, Lat]
 * @param resolution MGRS resolution (1-5)
 */
export function llToMgrs(point: [number, number], resolution: number = 1): string {
    return forward(point, resolution);
}

/**
 * Conversion from radians to degrees.
 */
function toDegrees(rad: number): number {
    return (rad / Math.PI) * 180;
}

/**
 * Conversion from degrees to radians.
 */
function degToRad(deg: number): number {
    return deg * (Math.PI / 180.0);
}

/**
 * Calculates the MGRS letter designator for the given latitude.
 */
export function getLetterDesignator(lat: number): string {
    let LetterDesignator = 'Z';

    if (84 >= lat && lat >= 72) {
        LetterDesignator = 'X';
    } else if (72 > lat && lat >= 64) {
        LetterDesignator = 'W';
    } else if (64 > lat && lat >= 56) {
        LetterDesignator = 'V';
    } else if (56 > lat && lat >= 48) {
        LetterDesignator = 'U';
    } else if (48 > lat && lat >= 40) {
        LetterDesignator = 'T';
    } else if (40 > lat && lat >= 32) {
        LetterDesignator = 'S';
    } else if (32 > lat && lat >= 24) {
        LetterDesignator = 'R';
    } else if (24 > lat && lat >= 16) {
        LetterDesignator = 'Q';
    } else if (16 > lat && lat >= 8) {
        LetterDesignator = 'P';
    } else if (8 > lat && lat >= 0) {
        LetterDesignator = 'N';
    } else if (0 > lat && lat >= -8) {
        LetterDesignator = 'M';
    } else if (-8 > lat && lat >= -16) {
        LetterDesignator = 'L';
    } else if (-16 > lat && lat >= -24) {
        LetterDesignator = 'K';
    } else if (-24 > lat && lat >= -32) {
        LetterDesignator = 'J';
    } else if (-32 > lat && lat >= -40) {
        LetterDesignator = 'H';
    } else if (-40 > lat && lat >= -48) {
        LetterDesignator = 'G';
    } else if (-48 > lat && lat >= -56) {
        LetterDesignator = 'F';
    } else if (-56 > lat && lat >= -64) {
        LetterDesignator = 'E';
    } else if (-64 > lat && lat >= -72) {
        LetterDesignator = 'D';
    } else if (-72 > lat && lat >= -80) {
        LetterDesignator = 'C';
    }
    return LetterDesignator;
}
